import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import MFormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import React, { CSSProperties, Fragment, ReactNode, useCallback, useMemo, useState } from 'react';
import Select, { components, OptionProps, SingleValueProps, MultiValueProps, GroupProps } from 'react-select';
import { Label, UncontrolledTooltip } from 'reactstrap';
import { useOptionalModalInstance } from '../ModalStack';
import { Theme } from '@core/utils/css';

const lightCustomStyles = {
  menuPortal: base => ({
    ...base,
    zIndex: 9999, // Ensure the portal is above other elements
  }),
};

const darkCustomStyles = {
  menuPortal: base => ({
    ...base,
    zIndex: 9999, // Ensure the portal is above other elements
  }),
  singleValue: base => ({
    ...base,
    color: '#bfc8e2',
  }),
  multiValue: base => ({
    ...base,
    color: '#FFF',
    backgroundColor: '#319cf4',
  }),
  multiValueLabel: base => ({
    ...base,
    color: '#FFF',
  }),
  control: base => ({
    ...base,
    backgroundColor: '#111', // Control background
    color: '#bfc8e2', // Control text
    borderColor: '#2a2a2a',
  }),
  menu: base => ({
    ...base,
    backgroundColor: '#111', // Menu background
    color: '#bfc8e2', // Menu text
    border: '1px solid #2a2a2a',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#319cf4'
      : state.isFocused
      ? '#deebff20' // Hover background
      : '#111',

    color: state.isSelected ? '#FFF' : state.isFocused ? '#FFF' : '#FFF',
  }),
};

export interface FormSelectItem<T extends string | boolean | number | any = any, D = any> {
  value: T;
  label: ReactNode | string;
  title?: ReactNode;
  delimiter?: boolean;
  options?: FormSelectItem<T>[];
  data?: D;
  isDisabled?: boolean;
}

export interface OptionItemProps<T> {
  item: FormSelectItem<any, T>;
}

export interface FormSelectBaseProps<T extends string | number | any | string[] | number[] | any[]> {
  onChange?(value: T | null): void;
  withoutGroup?: boolean;
  autofocus?: boolean;
  style?: CSSProperties;
  formGroupStyle?: CSSProperties;
  isClearable?: boolean;
  noOptionsMessage?: string;
  horizontal?: boolean;
  description?: string | ReactNode;
  container?: string;
  noLabel?: boolean;
  asterisk?: boolean;
  itemRenderer?: React.ComponentType<OptionItemProps<any>>;
  groupItemRenderer?: React.ComponentType<OptionItemProps<any>>;
  selectedItemRenderer?: React.ComponentType<OptionItemProps<any>>;
  hideLabel?: boolean;
}

export interface FormSelectSingleProps<T extends string | number | any> extends FormSelectBaseProps<T>, FieldProps<FormControl<T>> {
  items: FormSelectItem<T>[];
  type?: 'single';
  onAdd?(value: string): void;
  returnKeys?: string;
  disabled?: boolean;
}

export interface FormSelectMultipleProps<T extends string[] | number[]> extends FormSelectBaseProps<T>, FieldProps<FormControl<T>> {
  items: FormSelectItem<T[0]>[];
  type?: 'multiple';
  onAdd?(value: string): void;
  returnKeys?: string;
  maxSize?: number;
  disabled?: boolean;
}

export type FormSelectProps<T extends any> = FormSelectSingleProps<T> | FormSelectMultipleProps<any>;

export function FormSelect<T extends any>(props: FormSelectProps<T>) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);
  const [inputValue, setInputValue] = useState('');
  const modal = useOptionalModalInstance();

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const fieldReadonly = fieldConfig.readonly || props.readonly || false;
  const fieldDisabled = fieldConfig.disabled || props.disabled || false;

  const OptionItem = (myProps: OptionProps<any, boolean>) => {
    if (!props.itemRenderer) return <components.Option {...myProps} />;
    const C = props.itemRenderer;
    return (
      <components.Option {...myProps}>
        <C item={myProps.data} />
      </components.Option>
    );
  };

  const SelectedItem = (myProps: SingleValueProps<any>) => {
    if (!props.selectedItemRenderer) return <components.SingleValue {...myProps} />;
    const C = props.selectedItemRenderer;
    return (
      <components.SingleValue {...myProps}>
        <C item={myProps.data} />
      </components.SingleValue>
    );
  };

  const MultipleSelectedItem = (myProps: MultiValueProps<any>) => {
    if (!props.selectedItemRenderer) return <components.MultiValue {...myProps} />;
    const C = props.selectedItemRenderer;
    return (
      <components.MultiValue {...myProps}>
        <C item={myProps.data} />
      </components.MultiValue>
    );
  };

  const allItems = useMemo(() => {
    return R.flatMap(props.items, item => {
      if (item && item.options) {
        return item.options;
      }
      return [item];
    });
  }, [props.items]);

  const fieldValue = R.isNil(fieldState.value) ? '' : fieldState.value;

  const selectedValue = useMemo(() => {
    if (props.type === 'multiple' && fieldValue) {
      return R.pipe(
        allItems,
        R.ops.compact,
        R.ops.filter(i => (fieldValue as any[]).includes(i.value)),
      );
    } else {
      return R.find(allItems, i => (i ? i.value == fieldValue : null));
    }
  }, [props.type, allItems, fieldValue]);

  const onInputChange = useCallback(value => {
    setInputValue(value);
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      //props.type === 'multiple' &&
      if (props.onAdd) {
        const keyCodes = (props.returnKeys || '').split('').map(c => c.charCodeAt(0).toString());
        keyCodes.push('13');
        if (keyCodes.includes(event.keyCode.toString())) {
          props.onAdd(inputValue);
          setInputValue('');
        }
      }
    },
    [inputValue, props],
  );

  const tooltip = useMemo(() => {
    if (!props.description) return null;
    const uid = formControl.uid.replace('form:', '');
    return (
      <Fragment>
        <i className="fas fa-question-circle ml-2" id={`control-description-${uid}`}></i>
        <UncontrolledTooltip container={props.container} placement="top" target={`control-description-${uid}`}>
          {props.description}
        </UncontrolledTooltip>
      </Fragment>
    );
  }, [formControl.uid, props.container, props.description]);

  const content = (
    <React.Fragment>
      {!props.noLabel && (
        <Label style={props.horizontal ? { marginBottom: 0, marginRight: 10 } : {}}>
          {fieldConfig.label} {fieldConfig.required && props.asterisk && <span className="text-danger">*</span>}
          {fieldConfig.required && !R.isEmpty(fieldConfig.label) && (
            <span className="text-danger font-size-14" style={{ marginTop: -5 }}>
              *
            </span>
          )}
          {tooltip}
        </Label>
      )}
      <MFormControl error={!!fieldError} fullWidth>
        <Select
          onKeyDown={onKeyDown as any}
          onInputChange={onInputChange}
          inputValue={inputValue}
          autoFocus={props.autofocus}
          value={(selectedValue as any) || null}
          //   readOnly={fieldReadonly}
          //   disabled={fieldDisabled || props.disabled}
          isDisabled={fieldDisabled || props.disabled}
          onFocus={() => formControl.onFocus()}
          onBlur={() => formControl.onBlur()}
          placeholder={fieldConfig.placeholder}
          isClearable={props.isClearable}
          components={R.objectCompact({
            // GroupHeading: CustomGroupHeading,
            Option: props.itemRenderer ? OptionItem : undefined,
            SingleValue: props.selectedItemRenderer ? SelectedItem : undefined,
            MultiValue: props.selectedItemRenderer ? MultipleSelectedItem : undefined,
          })}
          onChange={value => {
            if (props.isClearable && value === null) {
              formControl.setValue(null);
              props.onChange && props.onChange(null);
            } else {
              if (props.type === 'multiple') {
                if (props.maxSize && value.length > props.maxSize && R.isArray(selectedValue)) {
                  formControl.setValue((selectedValue as any).map((v: any) => v.value));
                } else {
                  formControl.setValue(value.map((v: any) => v.value));
                  props.onChange && props.onChange(value.map((v: any) => v.value));
                }
              } else {
                formControl.setValue(value.value);
                props.onChange && props.onChange(value.value);
              }
            }
          }}
          styles={Theme.isDark ? darkCustomStyles : lightCustomStyles}
          menuPortalTarget={document.body}
          options={props.items}
          menuPosition={modal ? 'fixed' : undefined}
          className="select2-selection"
          classNamePrefix="select2-selection"
          isSearchable={true}
          isMulti={props.type === 'multiple'}
          noOptionsMessage={({ inputValue }) => (!!props.noOptionsMessage ? props.noOptionsMessage : 'No options')}
        />
        {!!fieldError && <FormHelperText className="mui-error">{fieldError}</FormHelperText>}
      </MFormControl>
    </React.Fragment>
  );

  if (props.withoutGroup) return content;
  return (
    <div style={props.formGroupStyle} className={`${props.horizontal ? 'd-flex align-items-center ' : 'form-group'}`}>
      {content}
    </div>
  );
}
